import type { ReactNode } from 'react';

import { cn } from '@/lib/utils';
import { formatCost } from '@/utils/formatCurrency.util';

interface CostChipProps {
  /** Label rendered before the value. Empty string renders value-only
   *  (e.g. when wrapped in a `<Field>` that already owns its label). */
  label: string;
  cost?: number | null;
  /** ISO currency code from the backend. USD is the contract; a non-USD code
   *  degrades to `—` (never a misleading `$`). Defaults to `'USD'` so callers
   *  that don't yet thread the field keep today's behavior. */
  currency?: string;
  /** True when the conversation/hunt has no recorded token usage — cost is
   *  unknown (old / never-run), not zero. Renders the `—` empty state with an
   *  explanatory tooltip instead of a misleading `$0.00`. */
  unknown?: boolean;
  isLoading: boolean;
  isError: boolean;
  className?: string;
}

/**
 * Presentational cost chip. Does NO data fetching — the parent passes
 * the hook result. States:
 *  - loading  → muted `…`
 *  - error    → `—` (never blocks the surface)
 *  - unknown  → `—` + "Cost not tracked …" tooltip (no recorded usage)
 *  - cost null (404 / unowned)  → renders nothing
 *  - non-USD currency  → `—` (USD-contract guard; never a wrong symbol)
 *  - success  → `formatCost(cost, currency)`
 * Chariot design tokens only.
 */
export function CostChip({
  label,
  cost,
  currency = 'USD',
  unknown,
  isLoading,
  isError,
  className,
}: CostChipProps) {
  const renderLabel = label ? (
    <span className="text-chariot-text-secondary text-sm font-medium">
      {label}
    </span>
  ) : null;

  const wrap = (value: ReactNode, title?: string) => (
    <span
      className={cn('flex items-center gap-2', className)}
      title={title}
      data-testid="cost-chip"
    >
      {renderLabel}
      {value}
    </span>
  );

  if (isLoading) {
    return wrap(
      <span
        className="text-chariot-text-secondary inline-block min-w-[4rem] text-sm tabular-nums"
        aria-busy="true"
      >
        <span aria-hidden="true">
          $<span className="ml-0.5 animate-pulse">•••</span>
        </span>
        <span className="sr-only">Calculating cost</span>
      </span>
    );
  }

  if (isError) {
    return wrap(
      <span className="text-chariot-text-secondary inline-block min-w-[4rem] text-sm tabular-nums">
        —
      </span>,
      'Cost unavailable'
    );
  }

  // Unknown → no usage was ever recorded (old / never-run conversation).
  // Render `—` with an explanatory tooltip rather than a misleading `$0.00`.
  if (unknown) {
    return wrap(
      <span className="text-chariot-text-secondary text-sm tabular-nums">
        —
      </span>,
      'Cost not tracked for this conversation'
    );
  }

  // 404 / no owned cost → render nothing (chip absent).
  if (cost == null) {
    return null;
  }

  // USD-contract guard: a non-USD code degrades to `—` rather than rendering
  // a misleading `$`. USD (the contract) formats byte-identically to before.
  const formatted = formatCost(cost, currency);
  if (formatted == null) {
    return wrap(
      <span className="text-chariot-text-secondary inline-block min-w-[4rem] text-sm tabular-nums">
        —
      </span>,
      'Cost unavailable'
    );
  }

  return wrap(
    <span className="text-chariot-text-primary inline-block min-w-[4rem] text-sm tabular-nums">
      {formatted}
    </span>
  );
}
